import * as Sentry from '@sentry/nextjs';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { RefObject } from 'react';
import { toast } from 'sonner';

import { getFirstConflictErrorMessage } from '@boss/boss-client';

import { submitForm } from './connector';

type Toast = {
  title: string;
  description?: string;
};

type DynamicFormHandle = {
  setFieldError: (field: string, message: string) => void;
  setFieldTouched: (field: string, isTouched: boolean, shouldValidate?: boolean) => void;
};

type Props = {
  toasts?: {
    error?: Toast;
    success?: Toast;
  };
  skipErrorToastOnConflict?: boolean;
  formRef?: RefObject<DynamicFormHandle>;
};

/**
 * useMutation implementation to submit a form.
 *
 * @returns {Object} An object with a mutate function and mutation status
 */
export const useSubmitForm = ({ toasts, skipErrorToastOnConflict, formRef }: Props = {}) => {
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async (formData: Record<string, unknown>) => await submitForm(formData, locale as string),
    onSuccess: () => {
      const successToast = toasts?.success;

      if (successToast) {
        toast.success(successToast.title, { description: successToast.description });
      }
    },
    onError: error => {
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          type: 'Submitting form',
        },
      });

      const isConflict = error && typeof error === 'object' && 'status' in error && error.status === 409;

      // If conflict and we have a form ref, set the field error on email field and skip showing global error
      if (isConflict && formRef?.current && 'setFieldError' in formRef.current) {
        const errorMessage = getFirstConflictErrorMessage(error) || 'Unable to process form';

        (formRef.current as { setFieldError: (field: string, message: string) => void }).setFieldError(
          'email',
          errorMessage,
        );
        (
          formRef.current as { setFieldTouched: (field: string, isTouched: boolean, shouldValidate?: boolean) => void }
        ).setFieldTouched('email', true, false);
        return;
      }

      if (skipErrorToastOnConflict && isConflict) {
        return;
      }

      const errorToast = toasts?.error;

      if (errorToast) {
        toast.error(errorToast.title, { description: errorToast.description });
      }
    },
  });
};
